import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Zap, TrendingDown, FileText, CheckCircle, Phone,
  ChevronDown, ArrowRight, Lightbulb, Target, PieChart, Clock,
  Building2, Home, Factory, DollarSign, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const AuditoriaEnergeticaPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const auditTypes = [
    {
      icon: Home,
      title: 'Auditoría Residencial',
      description: 'Análisis completo del consumo de tu hogar para identificar oportunidades de ahorro.',
      price: 'Desde USD 150',
      includes: ['Análisis de facturas', 'Medición de consumo', 'Identificación de pérdidas', 'Recomendaciones personalizadas']
    },
    {
      icon: Building2,
      title: 'Auditoría Comercial',
      description: 'Diagnóstico energético para negocios, oficinas y comercios.',
      price: 'Desde USD 500',
      includes: ['Análisis detallado', 'Termografía básica', 'Plan de eficiencia', 'ROI de mejoras']
    },
    {
      icon: Factory,
      title: 'Auditoría Industrial',
      description: 'Evaluación integral para plantas industriales y grandes consumidores.',
      price: 'A cotizar',
      includes: ['Medición en tiempo real', 'Análisis de procesos', 'Termografía avanzada', 'Consultoría especializada']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Recopilación de Datos',
      description: 'Analizamos tus facturas de los últimos 12 meses y visitamos tu instalación.'
    },
    {
      step: '02',
      title: 'Medición en Campo',
      description: 'Instalamos equipos de medición para capturar tu perfil de consumo real.'
    },
    {
      step: '03',
      title: 'Análisis Técnico',
      description: 'Nuestros ingenieros procesan los datos e identifican oportunidades.'
    },
    {
      step: '04',
      title: 'Informe Detallado',
      description: 'Entregamos un informe completo con diagnóstico y recomendaciones.'
    },
    {
      step: '05',
      title: 'Plan de Acción',
      description: 'Desarrollamos un plan priorizado con estimaciones de ahorro y costos.'
    }
  ];

  const savingsOpportunities = [
    {
      icon: Lightbulb,
      title: 'Iluminación Eficiente',
      saving: '30-50%',
      description: 'Cambio a LED y sensores de presencia'
    },
    {
      icon: Zap,
      title: 'Gestión de Cargas',
      saving: '15-25%',
      description: 'Optimización de horarios y demanda pico'
    },
    {
      icon: Target,
      title: 'Factor de Potencia',
      saving: '10-20%',
      description: 'Corrección de reactivos y multas'
    },
    {
      icon: TrendingDown,
      title: 'Energía Solar',
      saving: '85-100%',
      description: 'Generación propia de electricidad'
    }
  ];

  const faqs = [
    {
      question: '¿Qué es una auditoría energética?',
      answer: 'Es un análisis técnico detallado de cómo se consume la energía en una instalación. Permite identificar ineficiencias, pérdidas y oportunidades de ahorro, tanto económicas como ambientales.'
    },
    {
      question: '¿Cuánto tiempo toma una auditoría?',
      answer: 'Una auditoría residencial se completa en 1-2 semanas. Para comercios, entre 2-4 semanas. Proyectos industriales pueden requerir 1-2 meses dependiendo de la complejidad.'
    },
    {
      question: '¿Cuánto puedo ahorrar con una auditoría?',
      answer: 'En promedio, nuestros clientes logran reducir entre 20% y 40% de su consumo implementando las recomendaciones. El ahorro exacto depende de tu situación actual y las medidas implementadas.'
    },
    {
      question: '¿La auditoría incluye la implementación de mejoras?',
      answer: 'La auditoría incluye el diagnóstico y recomendaciones. La implementación se cotiza por separado. Ofrecemos descuentos especiales si implementás las mejoras con Zenex.'
    },
    {
      question: '¿Necesito parar mis operaciones para la auditoría?',
      answer: 'No. La mayor parte del trabajo se realiza sin interrumpir tus actividades. Solo algunas mediciones específicas pueden requerir coordinación de horarios.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <BarChart3 className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Auditoría Energética</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Conocé tu Consumo,
              <span className="text-[#90EE90]"> Optimizá tu Ahorro</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Diagnóstico profesional de tu consumo energético para identificar 
              oportunidades de eficiencia y ahorro. El primer paso hacia la energía inteligente.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20una%20auditoría%20energética"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Solicitar Auditoría
                <ArrowRight size={20} />
              </a>
              <button
                onClick={() => navigate('/calculadora')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                Calcular Ahorro Solar
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Opportunities */}
      <section className="py-12 bg-[#90EE90]">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <p className="text-zinc-800 font-bold text-lg">Oportunidades Típicas de Ahorro</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {savingsOpportunities.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center bg-white/80 rounded-xl p-4">
                  <Icon className="w-8 h-8 text-[#14532D] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-zinc-900">{item.saving}</p>
                  <p className="text-sm font-medium text-zinc-700">{item.title}</p>
                  <p className="text-xs text-zinc-500">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audit Types */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Tipos de Auditoría
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Soluciones para Cada Escala
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {auditTypes.map((audit, index) => {
              const Icon = audit.icon;
              return (
                <div key={index} className="bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:shadow-xl transition-all duration-300">
                  <div className="p-6">
                    <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                      <Icon className="w-6 h-6 text-[#14532D]" />
                    </div>
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{audit.title}</h3>
                    <p className="text-zinc-600 text-sm mb-4">{audit.description}</p>
                    
                    <div className="mb-4">
                      <p className="text-2xl font-bold text-[#14532D]">{audit.price}</p>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {audit.includes.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                          <CheckCircle className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    <a
                      href={`https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20la%20${encodeURIComponent(audit.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 bg-zinc-900 text-white rounded-xl font-bold text-sm hover:bg-zinc-800 transition-colors flex items-center justify-center"
                    >
                      Consultar
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestro Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              ¿Cómo Funciona?
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-[#90EE90] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-lg font-bold text-zinc-900">{item.step}</span>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-zinc-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
                Tu Informe
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                ¿Qué Incluye el Diagnóstico?
              </h2>
              
              <div className="space-y-4">
                {[
                  { icon: PieChart, text: 'Análisis detallado de consumo por área/equipo' },
                  { icon: AlertTriangle, text: 'Identificación de pérdidas y desperdicios' },
                  { icon: DollarSign, text: 'Estimación de ahorros potenciales' },
                  { icon: FileText, text: 'Recomendaciones técnicas priorizadas' },
                  { icon: Target, text: 'Plan de acción con cronograma sugerido' },
                  { icon: Clock, text: 'Cálculo de retorno de inversión por mejora' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                      <span className="text-zinc-300">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-2xl p-6 sm:p-8 border border-zinc-700">
              <h3 className="text-xl font-bold text-white mb-4">Ejemplo de Resultado</h3>
              <div className="space-y-4">
                <div className="bg-zinc-900 rounded-xl p-4">
                  <p className="text-sm text-zinc-400 mb-1">Consumo Actual</p>
                  <p className="text-2xl font-bold text-white">5,200 kWh/mes</p>
                </div>
                <div className="bg-zinc-900 rounded-xl p-4">
                  <p className="text-sm text-zinc-400 mb-1">Potencial de Ahorro</p>
                  <p className="text-2xl font-bold text-[#90EE90]">35%</p>
                </div>
                <div className="bg-zinc-900 rounded-xl p-4">
                  <p className="text-sm text-zinc-400 mb-1">Ahorro Mensual Estimado</p>
                  <p className="text-2xl font-bold text-[#90EE90]">Gs. 850.000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre Auditorías
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#14532D] to-[#166534]">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <BarChart3 className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Descubrí tu Potencial de Ahorro
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Solicitá una auditoría energética y empezá a ahorrar en tu próxima factura.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20solicitar%20una%20auditoría%20energética"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Solicitar Auditoría
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default AuditoriaEnergeticaPage;
